import { VariableAutocomplete } from '../VariableAutocomplete';

import type {
  AnalyticsNodeMeta,
  AgentNodeMeta,
  ClassifierNodeMeta,
  QualityNodeMeta,
  ReviewNodeMeta,
} from '../VariablePicker';

export interface SendLaterValue {
  sendAt?: string;
  offsetMinutes?: number;
  bookingId?: string;
}

interface SendLaterFieldsProps {
  value: SendLaterValue;
  onChange: (fields: Partial<SendLaterValue>) => void;
  /** Messages can be tied to a booking; calls have no such link today. */
  showBookingLink?: boolean;
  /** What the node does when nothing is scheduled. */
  immediateHint: string;
  responseFieldsSchema?: Record<string, unknown>;
  reviewNodeMetas?: ReviewNodeMeta[];
  classifierNodeMetas?: ClassifierNodeMeta[];
  qualityNodeMetas?: QualityNodeMeta[];
  agentNodeMetas?: AgentNodeMeta[];
  analyticsNodeMetas?: AnalyticsNodeMeta[];
}

/** "4h 30m before" — reading -270 as a duration is otherwise a mental exercise. */
const describeOffset = (minutes: number): string => {
  const abs = Math.abs(minutes);
  const hours = Math.floor(abs / 60);
  const mins = abs % 60;
  const parts = [hours ? `${hours}h` : '', mins ? `${mins}m` : ''].filter(Boolean).join(' ');
  return `${parts || '0m'} ${minutes < 0 ? 'before' : 'after'}`;
};

export const SendLaterFields = ({
  value,
  onChange,
  showBookingLink = false,
  immediateHint,
  responseFieldsSchema,
  reviewNodeMetas,
  classifierNodeMetas,
  qualityNodeMetas,
  agentNodeMetas,
  analyticsNodeMetas,
}: SendLaterFieldsProps) => {
  const offset = value.offsetMinutes ?? 0;

  return (
    <div className="space-y-3 rounded-md border p-3">
      <div>
        <label className="text-muted-foreground text-xs font-medium">Send at (optional)</label>
        <VariableAutocomplete
          value={value.sendAt ?? ''}
          onChange={(v) => onChange({ sendAt: v || undefined })}
          placeholder="{{booking.iso}}"
          responseFieldsSchema={responseFieldsSchema}
          reviewNodeMetas={reviewNodeMetas}
          classifierNodeMetas={classifierNodeMetas}
          qualityNodeMetas={qualityNodeMetas}
          agentNodeMetas={agentNodeMetas}
          analyticsNodeMetas={analyticsNodeMetas}
        />
        <p className="text-muted-foreground mt-1 text-[10px]">
          A date or datetime. Leave blank to {immediateHint}. Prefer a full instant such as{' '}
          {'{{booking.iso}}'} — a bare date means midnight.
        </p>
      </div>

      <div>
        <label className="text-muted-foreground text-xs font-medium">Offset (minutes)</label>
        <input
          type="number"
          className="border-input bg-background focus-visible:ring-ring w-full rounded-md border px-3 py-2 text-sm shadow-sm focus-visible:ring-1 focus-visible:outline-none"
          value={value.offsetMinutes ?? ''}
          onChange={(e) => {
            const raw = e.target.value;
            onChange({ offsetMinutes: raw === '' ? undefined : parseInt(raw, 10) || 0 });
          }}
          placeholder="-240"
        />
        <p className="text-muted-foreground mt-1 text-[10px]">
          Shifts the send time. Negative is before, e.g. -240 ={' '}
          <span className="font-medium">4h before</span>.
          {offset !== 0 && <> Currently {describeOffset(offset)} the time above.</>}
        </p>
      </div>

      {showBookingLink && (
        <div>
          <label className="text-muted-foreground text-xs font-medium">Booking (optional)</label>
          <VariableAutocomplete
            value={value.bookingId ?? ''}
            onChange={(v) => onChange({ bookingId: v || undefined })}
            placeholder="{{booking.id}}"
            responseFieldsSchema={responseFieldsSchema}
            reviewNodeMetas={reviewNodeMetas}
            classifierNodeMetas={classifierNodeMetas}
            qualityNodeMetas={qualityNodeMetas}
            agentNodeMetas={agentNodeMetas}
            analyticsNodeMetas={analyticsNodeMetas}
          />
          <p className="text-muted-foreground mt-1 text-[10px]">
            Ties the queued message to a booking, so cancelling or rescheduling that visit drops the
            reminder instead of sending it for a visit that moved.
          </p>
        </div>
      )}

      <p className="text-muted-foreground text-[10px]">
        Scheduled sends are resolved now and stored — see them under Scheduled Sends, where they can
        also be cancelled.
      </p>
    </div>
  );
};
